import { getLatestShoutTnaPoint, type ShoutTnaPoint, type ShoutTnaResponse } from '../api/chartDataTypes';
import { formatReportDate, reportTypeLabel } from '../utils/perf10kFormat';
import { buildShoutTnaSummarySentence } from '../utils/shoutTnaFormat';
import { useSubmittedSelection } from '../utils/useSubmittedSelection';
import { ChartDataSectionShell } from './ChartDataSectionShell';
import { DATE_POINT_SUBMIT_GUIDANCE, DataPointSelector } from './DataPointSelector';

type ShoutTnaChartPanelProps = {
    response: ShoutTnaResponse;
};

/**
 * Shares-Outstanding / Total-Net-Assets orchestrator. Mirrors Perf10kChartPanel
 * but adapts to the shout_tna payload, which has no `data.summary` block — the
 * default summary is built from the latest point in `data.series`, and the
 * user's date pick overrides it.
 */
export function ShoutTnaChartPanel({ response }: ShoutTnaChartPanelProps) {
    const { security, title, report_date, data, currency } = response;
    const latestPoint = getLatestShoutTnaPoint(response);

    // Null until the user submits a date pick; the panel falls back to
    // `latestPoint` for the default summary. The shell handles the NVDA
    // re-announcement whenever `summarySentence` changes.
    const [selectedPoint, announcementKey, handleSelectedPointChange] = useSubmittedSelection<ShoutTnaPoint>();

    const headerTitle = `${security.name} (${security.ticker}) — ${title}`;
    const headerSubtitle = `${reportTypeLabel(security.report_type)} · Report date: ${formatReportDate(report_date)}`;

    const pointForSummary = selectedPoint ?? latestPoint;
    const summaryParts = pointForSummary ? buildShoutTnaSummarySentence(pointForSummary, currency) : null;
    const summary = summaryParts?.text ?? 'No data available.';
    const summaryHTML = summaryParts?.html;

    return (
        <ChartDataSectionShell
            title={headerTitle}
            subtitle={headerSubtitle}
            summary={summary}
            summaryHTML={summaryHTML}
            announcementKey={announcementKey}
            guidance={DATE_POINT_SUBMIT_GUIDANCE}>
            {data.series.length > 0 ? (
                <DataPointSelector<ShoutTnaPoint>
                    points={data.series}
                    idPrefix='shout_tna'
                    onSelectedPointChange={handleSelectedPointChange}
                />
            ) : null}
        </ChartDataSectionShell>
    );
}
